import React, { ChangeEvent, FocusEvent, useRef, useState } from 'react';

import { Droplist, Icon } from '../index';

import { Props, Option } from './types';
import Input from '../Input/Input';
import OptionInputItem from './OptionInputItem';
import { DroplistContainer, Loader, SearchContainer } from './Select.styled';

const Select = React.forwardRef(
  (
    {
      name,
      options,
      onSearchInputChange,
      onSearchInputBlur,
      minSearchLengthToShowDroplist,
      onOptionClick,
      isLoading,
      inputRegexp,
      state,
      searchable = false,
      rightIcon,
      disabled,
      placeholder = 'Please select...',
      nullable = false,
      style,
      autoHeight = false,
      autoOpen = false,
      useOutsideClick,
      ...props
    }: Props,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isFocused, setFocused] = useState<boolean>(autoOpen);
    const isOptionSelected = (option: Option): boolean => {
      if (nullable && option.name === '' && props.value === null) {
        return true;
      }

      return props.value !== undefined && props.value !== null && option?.name.toString() === props.value.toString();
    };
    const selectedOptions = options?.filter((option) => isOptionSelected(option));
    const [inputValue, setInputValue] = useState<string>('');

    useOutsideClick &&
      useOutsideClick(containerRef, () => {
        setFocused(false);
      });

    const handleInputFocus = (e: FocusEvent<HTMLInputElement>) => {
      setFocused(true);
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      if (inputRegexp && value !== '' && !inputRegexp.test(value)) {
        return;
      }

      if (onSearchInputChange) {
        onSearchInputChange(value);
      }

      setInputValue(value);
    };

    const handleKeyDown = (event: any) => {
      if (!props.onChange) {
        return;
      }

      if (props.value !== undefined) {
        props.onChange(undefined);

        if (onSearchInputChange) {
          onSearchInputChange('');
        }
      }
    };

    const handleOptionClick = (option: Option, key: number) => {
      if (onOptionClick) {
        onOptionClick(option, key);
      }
      console.log('valueeeeeess');
      setInputValue('');
      setFocused(false);
    };

    const handleInputItemClick = async () => {
      if (inputRef) {
        inputRef?.current?.focus();
      }
      setTimeout(() => setFocused(true), 1);
    };

    const droplistOptions = options?.map((option) => ({
      ...option,
      selected: isOptionSelected(option),
    }));

    return (
      <SearchContainer ref={containerRef} disabled={disabled} autoHeight={autoHeight} searchable={searchable}>
        <Input
          ref={inputRef}
          name={name}
          leftIcon={searchable ? () => <Icon type="search" color="#3A4451" /> : undefined}
          rightIcon={
            isLoading
              ? () => <Loader />
              : rightIcon
              ? (stateColor) => rightIcon(stateColor, setFocused, isFocused)
              : (stateColor) => <Icon type="iconDown" color={stateColor || 'var(--color-smoke-70)'} />
          }
          autoComplete="off"
          onFocus={handleInputFocus}
          onFocusCapture={handleInputFocus}
          beforeInput={
            Boolean(selectedOptions?.length) && inputValue?.length === 0 ? (
              <OptionInputItem style={style} option={selectedOptions[0]} handleInputItemClick={handleInputItemClick} />
            ) : undefined
          }
          readOnly={!searchable}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          value={inputValue}
          onBlur={onSearchInputBlur}
          style={{ overflow: 'hidden' }}
          state={state}
          disabled={disabled}
          placeholder={Boolean(selectedOptions?.length === 0) && inputValue?.length === 0 ? placeholder : undefined}
        />

        {isFocused &&
          !isLoading &&
          (!minSearchLengthToShowDroplist ||
            (minSearchLengthToShowDroplist && inputValue?.length >= minSearchLengthToShowDroplist)) &&
          Boolean(options?.length) && (
            <DroplistContainer>
              <Droplist options={droplistOptions} onOptionClick={handleOptionClick} />
            </DroplistContainer>
          )}
      </SearchContainer>
    );
  },
);

export default Select;
